// Body Upgrade Effect (AAA Steampunk Gauntlet Animation - Multi-Stage)
// Narrative: 
// 1. MATERIALIZE: Runic matrix forms on ground; heavy gauntlet drops in.
// 2. LOCK_TARGET: Gauntlet hovers, gears spin, steam vents violently.
// 3. CHARGE: Massive canvas-drawn green aura builds; energy motes sucked into the core.
// 4. LASER_BEAM: A blinding green laser strikes the avatar.
// 5. INFUSION: Gauntlet slams down; massive shockwaves and sparks explode.
// 6. DISSIPATE: Gauntlet crumbles into cinders and mist.

import { Particle } from '../ParticleLayer';

const CONFIG = {
    scale: 1.0,
    materializeDur: 800,
    lockDur: 800,
    chargeDur: 1000,
    laserDur: 600,
    infusionDur: 1000,
    dissipateDur: 800,
    paths: {
        gauntlet: '/assets/effects/body_upgrade/gauntlet.svg'
    }
};

const spriteCache: Record<string, HTMLImageElement> = {};
const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};

getSprite(CONFIG.paths.gauntlet);

class UtilitySteam {
    x: number; y: number;
    vx: number; vy: number;
    life: number; size: number;
    maxLife: number; rotation: number; rotSpeed: number;

    constructor(x: number, y: number, angle: number, power: number = 1.0) {
        this.x = x; this.y = y;
        const speed = (2 + Math.random() * 4) * power;
        const spread = (Math.random() - 0.5) * 0.8;
        const emitAngle = angle + spread;
        this.vx = Math.cos(emitAngle) * speed;
        this.vy = Math.sin(emitAngle) * speed;
        this.maxLife = 600 + Math.random() * 600;
        this.life = this.maxLife;
        this.size = (15 + Math.random() * 25) * power;
        this.rotation = Math.random() * Math.PI * 2;
        this.rotSpeed = (Math.random() - 0.5) * 0.05;
    }
    update(dt: number) {
        const df = dt / 16.67;
        this.x += this.vx * df; this.y += this.vy * df;
        this.vx *= Math.pow(0.92, df); this.vy *= Math.pow(0.92, df); this.vy -= 0.6 * df;
        this.life -= dt; this.size += dt * 0.08; this.rotation += this.rotSpeed * df;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        const alpha = Math.sin((this.life / this.maxLife) * Math.PI) * 0.35;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(this.x, this.y); ctx.rotate(this.rotation);
        ctx.beginPath(); ctx.arc(0, 0, this.size, 0, Math.PI * 2);
        const grad = ctx.createRadialGradient(0, 0, 0, 0, 0, this.size);
        grad.addColorStop(0, 'rgba(255,255,255,0.9)');
        grad.addColorStop(0.5, 'rgba(200,200,200,0.5)');
        grad.addColorStop(1, 'rgba(150,150,150,0)');
        ctx.fillStyle = grad; ctx.fill();
        ctx.restore();
    }
}

class EnergyMote {
    x: number; y: number;
    targetX: number; targetY: number;
    life: number; size: number;
    color: string;

    constructor(targetX: number, targetY: number) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 300 + Math.random() * 300;
        this.x = targetX + Math.cos(angle) * dist;
        this.y = targetY + Math.sin(angle) * dist;
        this.targetX = targetX; this.targetY = targetY;
        this.life = 0; // counts up to 1
        this.size = 2 + Math.random() * 4;
        const colors = ['#4ade80', '#22c55e', '#86efac', '#ffffff'];
        this.color = colors[Math.floor(Math.random() * colors.length)];
    }
    update(dt: number, currentTargetY: number) {
        const df = dt / 16.67;
        this.life += dt * 0.002;
        this.targetY = currentTargetY;
        const dx = this.targetX - this.x;
        const dy = this.targetY - this.y;
        this.x += dx * 0.1 * df;
        this.y += dy * 0.1 * df;
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life >= 1) return;
        ctx.save();
        ctx.globalAlpha = this.life;
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath(); ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.shadowColor = this.color; ctx.shadowBlur = 10;
        ctx.fill(); ctx.restore();
    }
}

class SuperShockwave {
    x: number; y: number;
    radius: number = 0; maxRadius: number;
    life: number; maxLife: number;
    type: 'GREEN_PULSE' | 'IMPACT_RING';

    constructor(x: number, y: number, type: 'GREEN_PULSE' | 'IMPACT_RING') {
        this.x = x; this.y = y; this.type = type;
        this.maxRadius = type === 'IMPACT_RING' ? 250 : 150;
        this.maxLife = type === 'IMPACT_RING' ? 800 : 500;
        this.life = this.maxLife;
    }
    update(dt: number) {
        this.life -= dt;
        const p = 1 - (this.life / this.maxLife);
        this.radius = this.maxRadius * (1 - Math.pow(1 - p, 3)); // ease out cubic
    }
    draw(ctx: CanvasRenderingContext2D) {
        if (this.life <= 0) return;
        const alpha = Math.max(0, this.life / this.maxLife);
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.ellipse(this.x, this.y, this.radius, this.radius * 0.35, 0, 0, Math.PI * 2);

        if (this.type === 'GREEN_PULSE') {
            ctx.strokeStyle = '#22c55e';
            ctx.lineWidth = 15 * alpha;
            ctx.shadowColor = '#4ade80';
            ctx.shadowBlur = 20;
        } else {
            ctx.strokeStyle = '#fef08a';
            ctx.lineWidth = 25 * alpha;
            ctx.shadowColor = '#facc15';
            ctx.shadowBlur = 30;
        }
        ctx.stroke();
        ctx.restore();
    }
}

class TargetMatrix {
    x: number; y: number;
    rotation: number = 0;
    alpha: number = 0;

    constructor(x: number, y: number) {
        this.x = x; this.y = y;
    }

    draw(ctx: CanvasRenderingContext2D, targetAlpha: number, df: number = 1) {
        this.alpha += (targetAlpha - this.alpha) * 0.1 * df;
        this.rotation += 0.02 * df;

        if (this.alpha <= 0.01) return;

        ctx.save();
        ctx.globalAlpha = this.alpha * 0.5;
        ctx.globalCompositeOperation = 'screen';
        ctx.translate(this.x, this.y);
        ctx.scale(1, 0.4); // Iso perspective
        ctx.rotate(this.rotation);

        // Outer gear ring
        ctx.beginPath();
        for (let i = 0; i < 36; i++) {
            const a = (i * 10) * Math.PI / 180;
            const r = i % 2 === 0 ? 120 : 140;
            ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r);
        }
        ctx.closePath();
        ctx.strokeStyle = '#4ade80';
        ctx.lineWidth = 4;
        ctx.stroke();

        // Inner runes/circles
        ctx.rotate(-this.rotation * 2);
        ctx.beginPath(); ctx.arc(0, 0, 80, 0, Math.PI * 2);
        ctx.setLineDash([15, 10]);
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.restore();
    }
}

export type BodyUpgradeEffect = {
    kind: 'bodyUpgrade'
    t: number
    tx: number; ty: number;
    isOpponent?: boolean;
    data?: {
        phase: 'MATERIALIZE' | 'LOCK_TARGET' | 'CHARGE' | 'LASER_BEAM' | 'INFUSION' | 'DISSIPATE'
        steam: UtilitySteam[]
        motes: EnergyMote[]
        shockwaves: SuperShockwave[]
        matrix: TargetMatrix
        hoverY: number
    }
}

export const createBodyUpgradeEffect = (tx: number, ty: number, isOpponent?: boolean): BodyUpgradeEffect => ({
    kind: 'bodyUpgrade', t: 0, tx, ty, isOpponent
});

export const renderBodyUpgradeEffect = (
    eff: BodyUpgradeEffect,
    ctx: CanvasRenderingContext2D,
    spawnParticle: (p: Particle) => void
): boolean => {
    const inverted = eff.isOpponent ?? false;
    const effX = eff.tx;
    const effY = eff.ty;

    if (!eff.data) {
        eff.data = {
            phase: 'MATERIALIZE', steam: [], motes: [], shockwaves: [],
            matrix: new TargetMatrix(effX, effY + (inverted ? -40 : 40)),
            hoverY: effY + (inverted ? 180 : -180)
        };
    }

    const d = eff.data; const df = (eff as any)._df ?? 1; const dt = 16.7 * df;
    const tMat = CONFIG.materializeDur;
    const tLoc = tMat + CONFIG.lockDur;
    const tCha = tLoc + CONFIG.chargeDur;
    const tLas = tCha + CONFIG.laserDur;
    const tInf = tLas + CONFIG.infusionDur;
    const tTotal = tInf + CONFIG.dissipateDur;

    if (eff.t < tMat) d.phase = 'MATERIALIZE';
    else if (eff.t < tLoc) d.phase = 'LOCK_TARGET';
    else if (eff.t < tCha) d.phase = 'CHARGE';
    else if (eff.t < tLas) d.phase = 'LASER_BEAM';
    else if (eff.t < tInf) d.phase = 'INFUSION';
    else d.phase = 'DISSIPATE';

    ctx.save();
    let gauntletAlpha = 0;
    let gauntletScale = CONFIG.scale;
    let currentX = effX;
    let currentY = d.hoverY;
    let hoverOffset = Math.sin(eff.t * 0.005) * 12;
    let canvasAuraIntensity = 0;
    let matrixAlpha = 0;

    if (d.phase === 'MATERIALIZE') {
        const p = eff.t / tMat;
        const easeOut = 1 - Math.pow(1 - p, 4);
        gauntletAlpha = easeOut;
        const startDropY = effY + (inverted ? 800 : -800);
        currentY = startDropY + ((d.hoverY - startDropY) * easeOut);
        gauntletScale = 2.0 - easeOut; // massive then normals down
        matrixAlpha = p;

    } else if (d.phase === 'LOCK_TARGET') {
        gauntletAlpha = 1;
        currentY = d.hoverY + hoverOffset;
        matrixAlpha = 1;

        if (Math.random() < 0.6) {
            d.steam.push(new UtilitySteam(currentX - 50, currentY - 30, Math.PI - 0.3, 1.5));
            d.steam.push(new UtilitySteam(currentX + 50, currentY - 30, 0.3, 1.5));
        }

    } else if (d.phase === 'CHARGE') {
        gauntletAlpha = 1;
        matrixAlpha = 1;
        const p = (eff.t - tLoc) / CONFIG.chargeDur;
        currentY = d.hoverY + hoverOffset + (inverted ? (p * 30) : -(p * 30)); // rises slightly in power (or lowers if inverted)
        canvasAuraIntensity = p;

        ctx.translate((Math.random() - 0.5) * 2, (Math.random() - 0.5) * 2); // gentle screen shake

        if (eff.t % 3 < 1) d.motes.push(new EnergyMote(currentX, currentY + (inverted ? 20 : -20)));

    } else if (d.phase === 'LASER_BEAM') {
        gauntletAlpha = 1;
        matrixAlpha = 1;
        const p = (eff.t - tCha) / CONFIG.laserDur;
        currentY = d.hoverY + hoverOffset + (inverted ? 30 : -30);
        canvasAuraIntensity = 1;

        ctx.translate((Math.random() - 0.5) * 8, (Math.random() - 0.5) * 8); // heavy screen shake

    } else if (d.phase === 'INFUSION') {
        gauntletAlpha = 1;
        const p = (eff.t - tLas) / CONFIG.infusionDur;

        // Complex Slam Physics
        if (p < 0.15) {
            // Anticipation: Pull back up slowly
            const ap = p / 0.15;
            currentY = d.hoverY + (inverted ? 30 : -30) + (inverted ? (Math.sin(ap * Math.PI / 2) * 40) : -(Math.sin(ap * Math.PI / 2) * 40));
            gauntletScale = CONFIG.scale * (1 + (ap * 0.1)); // Stretch slightly upwards
        } else if (p < 0.25) {
            // The Violent Drop
            const dp = (p - 0.15) / 0.10;
            const easeIn = dp * dp * dp;
            const startY = d.hoverY + (inverted ? 70 : -70); // Peak of anticipation
            currentY = startY + (easeIn * (effY + (inverted ? -20 : 20) - startY)); // Drops past target slightly
            gauntletScale = CONFIG.scale * (1 - (dp * 0.1)); // Compress slightly on way down
        } else {
            // Post-Impact & Recoil
            const rp = (p - 0.25) / 0.75;

            // Recoil spring physics (overshoot, bounce back, settle)
            // Damped sine wave
            const bounce = Math.exp(-rp * 8) * Math.cos(rp * Math.PI * 6);
            currentY = effY + (inverted ? -10 : 10) + (inverted ? -(bounce * 15) : (bounce * 15));

            // Squash and Stretch upon impact
            const squash = Math.exp(-rp * 12) * Math.cos(rp * Math.PI * 4);
            gauntletScale = CONFIG.scale * (1 + (squash * 0.25));

            if (d.shockwaves.length === 0) {
                const swY = effY + (inverted ? -40 : 40);
                d.shockwaves.push(new SuperShockwave(effX, swY, 'IMPACT_RING'));
                setTimeout(() => d.shockwaves.push(new SuperShockwave(effX, swY, 'GREEN_PULSE')), 100);
                setTimeout(() => d.shockwaves.push(new SuperShockwave(effX, swY, 'GREEN_PULSE')), 300);
            }

            // Intense diminishing camera shake on impact
            const shakeIn = Math.max(0, 1 - (rp * 2.5));
            ctx.translate((Math.random() - 0.5) * 20 * shakeIn, (Math.random() - 0.5) * 20 * shakeIn);
        }

        matrixAlpha = 1 - p;
        canvasAuraIntensity = Math.max(0, 1 - (p * 1.5)); // Fades out faster after strike

        // Massive burst of ground sparks at exact moment of impact
        if (p >= 0.25 && p < 0.3 && Math.random() < 0.9) {
            for (let i = 0; i < 8; i++) {
                spawnParticle({
                    x: effX + (Math.random() - 0.5) * 120,
                    y: effY + (inverted ? -40 : 40),
                    vx: (Math.random() - 0.5) * 25,
                    vy: (inverted ? 5 : -5) + Math.random() * (inverted ? 25 : -25),
                    life: 600 + Math.random() * 600,
                    color: i % 2 === 0 ? '#4ade80' : '#fef08a',
                    size: 3 + Math.random() * 6
                });
            }
            // Side-venting impact steam
            if (Math.random() < 0.3) {
                d.steam.push(new UtilitySteam(currentX - 60, currentY, Math.PI, 2.0));
                d.steam.push(new UtilitySteam(currentX + 60, currentY, 0, 2.0));
            }
        }

    } else if (d.phase === 'DISSIPATE') {
        const p = (eff.t - tInf) / CONFIG.dissipateDur;
        gauntletAlpha = 1 - p;
        currentY = effY + (inverted ? -10 : 10) + (inverted ? p * 40 : -p * 40);
        matrixAlpha = 0;

        if (Math.random() < 0.5 && gauntletAlpha > 0) {
            spawnParticle({
                x: currentX + (Math.random() - 0.5) * 150,
                y: currentY + (Math.random() - 0.5) * 150,
                vx: (Math.random() - 0.5) * 5, vy: (inverted ? 5 : -5) + Math.random() * (inverted ? 5 : -5),
                life: 400 + Math.random() * 400, color: '#f97316', size: 3
            });
            d.steam.push(new UtilitySteam(currentX + (Math.random() - 0.5) * 100, currentY, -Math.PI / 2, 0.5));
        }
    }

    // --- RENDER ORDER ---

    d.matrix.draw(ctx, matrixAlpha, df);

    d.shockwaves.forEach(sw => sw.draw(ctx));

    // Thick Laser Beam Behind Gauntlet
    if (d.phase === 'LASER_BEAM') {
        const lp = (eff.t - tCha) / CONFIG.laserDur; // 0 to 1
        const beamW = 60 + Math.sin(lp * Math.PI * 10) * 10;
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        const grad = ctx.createLinearGradient(effX - beamW / 2, 0, effX + beamW / 2, 0);
        grad.addColorStop(0, 'rgba(34, 197, 94, 0)');
        grad.addColorStop(0.2, 'rgba(74, 222, 128, 0.8)');
        grad.addColorStop(0.5, 'rgba(255, 255, 255, 1)');
        grad.addColorStop(0.8, 'rgba(74, 222, 128, 0.8)');
        grad.addColorStop(1, 'rgba(34, 197, 94, 0)');
        ctx.fillStyle = grad;
        ctx.fillRect(effX - beamW / 2, currentY, beamW, (effY + (inverted ? -40 : 40)) - currentY);
        ctx.restore();
    }

    d.steam = d.steam.filter(s => s.life > 0);
    d.steam.forEach(s => { s.update(dt); s.draw(ctx); });

    // MASSIVE native canvas aura (prevents SVG bounding box clipping)
    const auraRadius = Math.max(0, 250 * canvasAuraIntensity + (Math.sin(eff.t * 0.01) * 20));
    if (canvasAuraIntensity > 0 && auraRadius > 0) {
        ctx.save();
        ctx.globalCompositeOperation = 'screen';
        ctx.beginPath();
        ctx.arc(currentX, currentY - 20, auraRadius, 0, Math.PI * 2);
        const aGrad = ctx.createRadialGradient(currentX, currentY - 20, 0, currentX, currentY - 20, auraRadius);
        aGrad.addColorStop(0, `rgba(134, 239, 172, ${canvasAuraIntensity * 0.8})`);
        aGrad.addColorStop(0.3, `rgba(34, 197, 94, ${canvasAuraIntensity * 0.5})`);
        aGrad.addColorStop(0.7, `rgba(21, 128, 61, ${canvasAuraIntensity * 0.2})`);
        aGrad.addColorStop(1, 'rgba(5, 46, 22, 0)');
        ctx.fillStyle = aGrad;
        ctx.fill();
        ctx.restore();
    }

    // SVG rendering
    if (gauntletAlpha > 0) {
        ctx.save();
        ctx.globalAlpha = gauntletAlpha < 0 ? 0 : gauntletAlpha;
        ctx.translate(currentX, currentY);
        ctx.scale(gauntletScale, gauntletScale);

        const sway = Math.sin(eff.t * 0.002) * 0.05 + (inverted ? Math.PI : 0);
        ctx.rotate(sway);

        const img = getSprite(CONFIG.paths.gauntlet);
        if (img.complete && img.naturalWidth > 0) {
            const aspect = img.naturalWidth / img.naturalHeight;
            const renderWidth = 300;
            const renderHeight = renderWidth / aspect;
            ctx.drawImage(img, -renderWidth / 2, -renderHeight / 2, renderWidth, renderHeight);
        }
        ctx.restore();
    }

    // Motes overlay
    d.motes = d.motes.filter(m => m.life < 1 && Math.hypot(m.x - currentX, m.y - currentY) > 20);
    d.motes.forEach(m => { m.update(dt, currentY - 20); m.draw(ctx); });

    d.shockwaves = d.shockwaves.filter(sw => sw.life > 0);
    d.shockwaves.forEach(sw => sw.update(dt));

    ctx.restore();
    return eff.t < tTotal;
};
